import classNames from 'classnames';
import type { CSSProperties, ReactNode } from 'react';

import { css } from '@sorare/css';

const ALL_POSITIONS = [
  'top-left',
  'top-right',
  'bottom-right',
  'bottom-left',
] as const;

const TOP_POSITIONS = ['top-left', 'top-right'] as const;

const DEFAULT_CORNER_SIZE = 8;

const root = css`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const corner = css`
  position: absolute;
  z-index: 1;
  pointer-events: none;

  &.top-left {
    top: 0;
    left: 0;
  }
  &.top-right {
    top: 0;
    right: 0;
    transform: rotate(90deg);
  }
  &.bottom-right {
    bottom: 0;
    right: 0;
    transform: rotate(180deg);
  }
  &.bottom-left {
    bottom: 0;
    left: 0;
    transform: rotate(270deg);
  }
`;

type CornerProps = {
  className: string;
  size: number;
  color?: string;
};

const Corner = ({ className, size, color }: CornerProps) => (
  <svg
    className={classNames(corner, className)}
    style={{ color }}
    width={size}
    height={size}
    viewBox="0 0 35.4751 34.689"
    preserveAspectRatio="none"
    aria-hidden
  >
    <path
      d="M8.76205 0L0 8.76205V34.689L5.24046 31.1179V18.9266L4.59541 18.496V10.1293L10.0179 4.70689H18.3788L18.8971 5.24046H31.9487L35.4751 0H8.76205Z"
      fill="currentColor"
    />
  </svg>
);

type Props = {
  children?: ReactNode;
  topOnly?: boolean;
  color?: string;
  /** Clockwise from the top-left corner. */
  colors?: [string, string, string, string];
  cornerSize?: number;
  className?: string;
  style?: CSSProperties;
};

export const FrameCorners = ({
  children,
  topOnly,
  cornerSize = DEFAULT_CORNER_SIZE,
  color,
  colors,
  className,
  style,
}: Props) => {
  const positions = topOnly ? TOP_POSITIONS : ALL_POSITIONS;

  return (
    <div className={classNames(root, className)} style={style}>
      {positions.map((position, i) => (
        <Corner
          key={position}
          className={position}
          size={cornerSize}
          color={colors?.[i] ?? color}
        />
      ))}
      {children}
    </div>
  );
};
